import {
  Component,
  Type,
  OnDestroy,
  AfterViewInit,
  ComponentRef,
  ViewChild,
  ComponentFactoryResolver,
  ChangeDetectorRef,
  ElementRef,
  HostListener,
  Inject,
  NgZone
} from '@angular/core';
import { Subject } from 'rxjs';
import { InsertionDirective } from './insertion.directive';
import { DialogRef } from './is-dialog-ref';
import { ISDialogStyleHandlerService } from './is-dialog-style-handler.service';
import { DialogConfig, TemplateLayouts } from './is-dialog-config';
import { FormControl } from '@angular/forms';
import { CdkDrag, CdkDragMove, CdkDragEnd } from '@angular/cdk/drag-drop';
import { DialogGeometry, DialogSnapZone, DialogViewport, DialogResizeEdge, fitDialogToViewport, geometryForSnap, detectDialogDragSnap } from './is-dialog-geometry';
import { Overlay, OverlayContainer } from '@angular/cdk/overlay';
import { ISDialogPipOverlayContainer } from './is-dialog-pip-overlay-container';
import { ISDialogGenieAnimationService, GenieAnimation, GenieRect } from './is-dialog-genie-animation.service';
import { MatMenuTrigger } from '@angular/material/menu';

/** Tipagem local: o TS DOM das versões anteriores ao Chrome 116 pode não incluir a API. */
interface DocumentPictureInPictureManager {
  readonly window: Window | null;
  requestWindow(options: { width: number; height: number }): Promise<Window>;
}

interface WindowWithDocumentPip extends Window {
  documentPictureInPicture?: DocumentPictureInPictureManager;
}

@Component({
  templateUrl: './is-dialog.component.html',
  styleUrls: ['./is-dialog.component.scss'],
  // Overlay local, para não deslocar os menus dos outros dialogs do CRM.
  providers: [
    ISDialogPipOverlayContainer,
    { provide: OverlayContainer, useExisting: ISDialogPipOverlayContainer },
    Overlay
  ]
})
export class ISDialogComponent implements AfterViewInit, OnDestroy {
  @ViewChild('dragHandle') dragBar!: ElementRef;
  componentId: string = '';
  tabId: string = '';
  windowBarId: string = '';
  tabTitleId: string = '';

  editTitle: boolean = false;

  dialogWidth: number | null = null;
  dialogHeight: number | null = null;

  dragPosition = { x: 0, y: 0 };

  isMinimized: boolean = false;
  isMaximized: boolean = false;

  /** Container Queries apenas dentro do corpo do dialog (variante experimental). */
  bootstrapGridEnabled = false;

  componentRef!: ComponentRef<any>;
  currentOpacity: number = 1;

  childComponentType!: Type<any>;

  @ViewChild(InsertionDirective) insertionPoint!: InsertionDirective;
  @ViewChild(CdkDrag) cdkDrag!: CdkDrag;
  @ViewChild('windowActionsTrigger') windowActionsTrigger?: MatMenuTrigger;

  /** O único estado geométrico usado por drag, resize, maximização e snap. */
  private freeGeometry: DialogGeometry | null = null;
  private snapZone: DialogSnapZone | null = null;
  private dragPreviewZone: DialogSnapZone | null = null;
  private draggingFromSnap = false;
  /** Um snap é arrastado sem CDK: evita mudar o retângulo com DragRef ativo. */
  isManualSnappedDrag = false;
  get isSnapped(): boolean { return !!this.snapZone; }
  private manualSnapDrag: {
    pointerId: number;
    originalZone: DialogSnapZone;
    startX: number;
    startY: number;
    ratioX: number;
    gripY: number;
    width: number;
    height: number;
    started: boolean;
    exitedOriginalZone: boolean;
    clientX: number;
    clientY: number;
    frame: number | null;
    captureElement: HTMLElement;
  } | null = null;
  private snapPreview: HTMLElement | null = null;
  private activeGenie: GenieAnimation | null = null;
  private genieSequence = 0;
  private openTimer: ReturnType<typeof setTimeout> | null = null;
  isOpening = true;
  isMinimizing = false;
  isRestoring = false;
  isResizing = false;

  /** Cache: 8 handles deixam de provocar leituras de offsetWidth/Height por CD. */
  private geometryCache: DialogGeometry = { x: 0, y: 0, width: 700, height: 420 };
  private openingGeometry: DialogGeometry | null = null;
  private intrinsicSizeObserver: ResizeObserver | null = null;
  get currentGeometry(): DialogGeometry { return this.geometryCache; }
  get isWindowActive(): boolean { return this._dialogStyleService.activeWindow?.id === this.componentId; }
  get isPinned(): boolean { return !!this._dialogStyleService.getWindow(this.componentId)?.pinned; }


  readonly resizeHandles: DialogResizeEdge[] = [
    'top', 'right', 'bottom', 'left',
    'top-left', 'top-right', 'bottom-left', 'bottom-right'
  ];

  get minResizeWidth(): number { return this.getMinWidth(); }
  get minResizeHeight(): number { return this.getMinHeight(); }
  get canResize(): boolean { return !this.isPictureInPicture && !this.isMaximized; }

  isPictureInPicture = false;
  pictureInPictureError: string | null = null;
  private pipWindow: Window | null = null;
  private pipPlaceholder: Comment | null = null;
  private pipPageHideHandler: (() => void) | null = null;
  private pipStylesObserver: MutationObserver | null = null;
  private destroyed = false;

  get isPictureInPictureSupported(): boolean {
    const api = (window as WindowWithDocumentPip).documentPictureInPicture;
    return window.isSecureContext && !!api?.requestWindow && this.data?.allowPictureInPicture !== false;
  }


  private readonly _onClose = new Subject<any>();
  public onClose = this._onClose.asObservable();

  auxTitle = new FormControl<any>(null);

  itsOpenClick: boolean = true;

  constructor(
    private hostElement: ElementRef<HTMLElement>,
    private genie: ISDialogGenieAnimationService,
    private zone: NgZone,
    private pipOverlayContainer: ISDialogPipOverlayContainer,
    private componentFactoryResolver: ComponentFactoryResolver,
    private cd: ChangeDetectorRef,
    public dialogRef: DialogRef,
    public data: DialogConfig,
    private _dialogStyleService: ISDialogStyleHandlerService,
    private _ref: ChangeDetectorRef,
    @Inject('MV_SPINNER_TEMPLATE') public spinnerTemplate: string,
  ) {
  }

  @HostListener('window:click', ['$event'])
  onScreenClick(event: MouseEvent) {
    if (!this.itsOpenClick) {
      //if (this._dialogStyleService.onMouseClick(event.target)) {
      //this.onMinimize();
      //}
    } else {
      this.itsOpenClick = false;
    }
  }

  @HostListener('window:resize')
  onViewportResize(): void {
    if (this.isPictureInPicture || this.destroyed) return;
    const viewport = this.getViewport();
    if (this.snapZone) {
      this.setGeometry(geometryForSnap(this.snapZone, viewport));
    } else {
      this.setGeometry(fitDialogToViewport(this.freeGeometry || this.currentGeometry, viewport, this.getMinWidth(), this.getMinHeight()));
    }
    this.hideSnapPreview();
  }

  ngOnInit(): void {
    this.componentId = `dialog-${this.generateUniqueId()}`;
    this.tabId = `tab-${this.generateUniqueId()}`;
    this.windowBarId = `windowBar-${this.generateUniqueId()}`;
    this.tabTitleId = `windowBar-${this.generateUniqueId()}`;
    this._dialogStyleService.addDialog(this.componentId);
    this._dialogStyleService.registerWindow({
      id: this.componentId,
      title: this.data.title || 'Janela',
      icon: this.data.iconTitle,
      minimized: false,
      inPictureInPicture: false,
      restore: () => this.reopenDialog(),
      minimize: () => this.onMinimize(),
      close: () => this.closeDialog(),
      focus: () => this.isPictureInPicture ? this.pipWindow?.focus() : this.onDialogClicked(),
      arrange: rect => this.arrangeInWorkspace(rect),
      snap: zone => this.applySnap(zone),
      center: () => this.centerDialog(),
      resetSize: () => this.resetDialogSize()
    });
    this.isMinimized = false;
    this.dialogRef._attach(this);
  }

  ngAfterViewInit(): void {
    this.loadChildComponent(this.childComponentType);
    this.configureTemplate();
    this.configureDialog();
    this.enableBootstrapContainerQueries();
    this.watchIntrinsicSize();
    this.cd.detectChanges();
    this.openTimer = setTimeout(() => {
      this.isOpening = false;
      if (!this.destroyed) this.cd.detectChanges();
    }, 200);

    if (this.data?.minimized) {
      Promise.resolve().then(() => { if (!this.destroyed) this.onMinimize(true); });
    }
  }

  /**
   * Aplica as regras de Container Queries DEPOIS da medição intrínseca inicial.
   * O container-type: inline-size anula a contribuição intrínseca na largura:
   * preservar a largura já medida impede dialogs auto de encolherem ao ativar.
   * O resize/snap seguintes continuam a alterar dialogWidth normalmente.
   */
  private enableBootstrapContainerQueries(): void {
    if (this.data.bootstrapContainerQueries === false) return;
    if (this.dialogWidth === null) {
      const naturalWidth = this.getDialogElement()?.getBoundingClientRect().width
        || this.currentGeometry.width;
      const width = Math.round(naturalWidth);
      if (Number.isFinite(width) && width > 0) {
        this.dialogWidth = width;
        this.geometryCache = { ...this.geometryCache, width };
        if (this.freeGeometry) this.freeGeometry = { ...this.freeGeometry, width };
        if (this.openingGeometry) this.openingGeometry = { ...this.openingGeometry, width };
      }
    }
    this.bootstrapGridEnabled = true;
    this.cd.detectChanges();
  }

  configureTemplate() {
    if (this.data.template) {
      switch (this.data.template) {
        case TemplateLayouts.entities:
          this.data.dialogConfig = {
            dialogColor: 'white',
            dialogBackgroundColor: '#4a74ae',
            iconColor: 'white',
            iconHoverColor: 'grey'
          };

          this.data.tabConfig = {
            tabBackGroundColor: '#4a74ae',
            tabBorderColor: '#4a74ae',
            iconColor: 'white',
            iconHoverColor: 'grey',
            tabColor: 'white'
          };
          break;
        case TemplateLayouts.tickets:
          this.data.dialogConfig = {
            dialogColor: 'white',
            dialogBackgroundColor: '#f9c927',
            iconColor: 'white',
            iconHoverColor: 'grey'
          };

          this.data.tabConfig = {
            tabBackGroundColor: '#f9c927',
            tabBorderColor: '#f9c927',
            iconColor: 'white',
            iconHoverColor: 'grey',
            tabColor: 'white'
          };
          break;
        case TemplateLayouts.leads:
          this.data.dialogConfig = {
            dialogColor: 'white',
            dialogBackgroundColor: '#1abc9c',
            iconColor: 'white',
            iconHoverColor: 'grey'
          };

          this.data.tabConfig = {
            tabBackGroundColor: '#1abc9c ',
            tabBorderColor: '#1abc9c ',
            iconColor: 'white',
            iconHoverColor: 'white',
            tabColor: 'white '
          };
          break;
        case TemplateLayouts.opportunities:
          this.data.dialogConfig = {
            dialogColor: 'white',
            dialogBackgroundColor: '#8e44ad',
            iconColor: 'white',
            iconHoverColor: 'grey'
          };

          this.data.tabConfig = {
            tabBackGroundColor: '#8e44ad ',
            tabBorderColor: '#8e44ad ',
            iconColor: 'white',
            iconHoverColor: 'white',
            tabColor: 'white '
          };
          break;
        case TemplateLayouts.history:
          this.data.dialogConfig = {
            dialogColor: 'white',
            dialogBackgroundColor: '#fe9952',
            iconColor: 'white',
            iconHoverColor: 'grey'
          };

          this.data.tabConfig = {
            tabBackGroundColor: '#fe9952',
            tabBorderColor: '#fe9952',
            iconColor: 'white',
            iconHoverColor: 'white',
            tabColor: 'white '
          };
          break;
        case TemplateLayouts.black:
          this.data.dialogConfig = {
            dialogColor: 'white',
            dialogBackgroundColor: 'black',
            iconColor: 'white',
            iconHoverColor: 'grey'
          };

          this.data.tabConfig = {
            tabBackGroundColor: 'black',
            tabBorderColor: 'black',
            iconColor: '#a1a5b7',
            iconHoverColor: 'white',
            tabColor: 'white'
          };
          break;
        case TemplateLayouts.white:
          this.data.dialogConfig = {
            dialogColor: '#7f8298',
            dialogBackgroundColor: 'white',
            iconColor: 'grey',
            iconHoverColor: 'black'
          };

          this.data.tabConfig = {
            tabBackGroundColor: 'white',
            tabBorderColor: 'grey',
            iconColor: 'grey',
            iconHoverColor: 'black',
            tabColor: 'grey'
          };
          break;
        case TemplateLayouts.primary:
          this.data.dialogConfig = {
            dialogColor: 'white',
            dialogBackgroundColor: '#2F3C4D',
            iconColor: 'rgba(255, 255, 255, 0.2)',
            iconHoverColor: 'white'
          };

          this.data.tabConfig = {
            tabBackGroundColor: '#2F3C4D',
            tabBorderColor: '#2F3C4D',
            iconColor: 'rgba(255, 255, 255, 0.2)',
            iconHoverColor: 'white',
            tabColor: 'white'
          };
          break;
        case TemplateLayouts.grey:
          this.data.dialogConfig = {
            dialogColor: 'white',
            dialogBackgroundColor: 'rgb(145 156 171)',
            iconColor: 'white',
            iconHoverColor: '#545454'
          };

          this.data.tabConfig = {
            tabBackGroundColor: '#929caa',
            tabBorderColor: '#929caa',
            iconColor: 'white',
            iconHoverColor: '#545454',
            tabColor: 'white'
          };
          break;
        case TemplateLayouts.lightPrimary:
          this.data.dialogConfig = {
            dialogColor: 'white',
            dialogBackgroundColor: 'rgb(176 208 251)',
            iconColor: 'white',
            iconHoverColor: '#767676'
          };

          this.data.tabConfig = {
            tabBackGroundColor: 'rgb(176 208 251)',
            tabBorderColor: 'rgb(176 208 251)',
            iconColor: 'white',
            iconHoverColor: '#767676',
            tabColor: 'white'
          };
          break;
        case TemplateLayouts.lightGrey:
          this.data.dialogConfig = {
            dialogColor: 'rgb(128 130 145)',
            dialogBackgroundColor: 'rgb(227 227 227)',
            iconColor: '#7f8298',
            iconHoverColor: 'black'
          };

          this.data.tabConfig = {
            tabBackGroundColor: 'rgb(249 249 249)',
            tabBorderColor: 'rgb(249 249 249)',
            iconColor: '#7f8298',
            iconHoverColor: 'black',
            tabColor: 'rgb(128 130 145)'
          };
          break;
        case TemplateLayouts.bankAccounts:
          this.data.dialogConfig = {
            dialogColor: 'white',
            dialogBackgroundColor: 'rgb(255 86 17)',
            iconColor: 'white',
            iconHoverColor: 'black'
          };

          this.data.tabConfig = {
            tabBackGroundColor: 'rgb(255 86 17)',
            tabBorderColor: 'rgb(255 86 17)',
            iconColor: 'white',
            iconHoverColor: 'black',
            tabColor: 'white'
          };
          break;
        case TemplateLayouts.accessRequests:
          this.data.dialogConfig = {
            dialogColor: 'white',
            dialogBackgroundColor: '#0C6832',
            iconColor: 'white',
            iconHoverColor: 'black'
          };

          this.data.tabConfig = {
            tabBackGroundColor: '#0C6832',
            tabBorderColor: '#0C6832',
            iconColor: 'white',
            iconHoverColor: 'black',
            tabColor: 'white'
          };
          break;
        case TemplateLayouts.darkBlue:
          this.data.dialogConfig = {
            dialogColor: 'white',
            dialogBackgroundColor: '#505DA6',
            iconColor: 'white',
            iconHoverColor: 'grey'
          };

          this.data.tabConfig = {
            tabBackGroundColor: '#505DA6',
            tabBorderColor: '#505DA6',
            iconColor: 'white',
            iconHoverColor: 'grey',
            tabColor: 'white'
          };
          break;
        default:
          break;
      }
    }
  }

  private getViewport(): DialogViewport {
    return { width: window.innerWidth, height: window.innerHeight };
  }

  private getMinWidth(): number { return Math.max(180, this.data.minWidth ?? 320); }
  private getMinHeight(): number { return Math.max(120, this.data.minHeight ?? 180); }

  configureDialog(): void {
    const element = this.getDialogElement();
    if (!element) return;
    const size = element.getBoundingClientRect();
    const viewport = this.getViewport();
    const autoWidth = this.data.width == null;
    const autoHeight = this.data.height == null;
    const desiredWidth = this.data.width ?? Math.max(Math.min(500, viewport.width), size.width);
    const desiredHeight = this.data.height ?? Math.max(180, size.height);
    const centeredX = Math.max(0, (viewport.width - desiredWidth) / 2);
    const centeredY = Math.max(0, (viewport.height - desiredHeight) / 2);
    this.setGeometry(fitDialogToViewport({
      x: this.data.initialPositionX ?? centeredX,
      y: this.data.initialPositionY ?? centeredY,
      width: desiredWidth,
      height: desiredHeight
    }, viewport, this.getMinWidth(), this.getMinHeight()));
    // Compatibilidade com dialogs existentes sem width/height na configuração:
    // continuam com tamanho automático até o utilizador mover/resize/snap.
    if (autoWidth) this.dialogWidth = null;
    if (autoHeight) this.dialogHeight = null;
    // As dimensões auto são medidas só uma vez, depois da primeira renderização.
    this.cd.detectChanges();
    const actual = element.getBoundingClientRect();
    this.geometryCache = {
      x: this.dragPosition.x, y: this.dragPosition.y,
      width: autoWidth ? (actual.width || desiredWidth) : this.dialogWidth!,
      height: autoHeight ? (actual.height || desiredHeight) : this.dialogHeight!
    };
    this.freeGeometry = { ...this.currentGeometry };
    this.openingGeometry = { ...this.currentGeometry };

    const dialogBar = this.hostElement.nativeElement.querySelector<HTMLElement>('#' + this.windowBarId);
    if (dialogBar) {
      dialogBar.style.color = this.data.dialogConfig?.dialogColor ?? 'white';
      dialogBar.style.backgroundColor = this.data.dialogConfig?.dialogBackgroundColor ?? '#2F3C4D';
    }
    const icons = element.getElementsByClassName('toolbar-buttons');
    for (let i = 0; i < icons.length; i++) {
      const icon = icons[i] as HTMLElement;
      icon.style.color = this.data.dialogConfig?.iconColor ?? 'rgba(255,255,255,.2)';
      icon.onmouseover = () => { icon.style.color = this.data.dialogConfig?.iconHoverColor ?? 'white'; };
      icon.onmouseleave = () => { icon.style.color = this.data.dialogConfig?.iconColor ?? 'rgba(255,255,255,.2)'; };
    }
    if (this.data.title) this.auxTitle.setValue(this.data.title);
    this.cd.detectChanges();
  }

  /** Para janelas com tamanho auto, a dimensão pode mudar após receber dados async. */
  private watchIntrinsicSize(): void {
    const element = this.getDialogElement();
    if (!element || typeof ResizeObserver === 'undefined' || (this.data.width != null && this.data.height != null)) return;
    this.intrinsicSizeObserver = new ResizeObserver(entries => {
      if (this.destroyed || this.isMinimized || this.isPictureInPicture || !!this.snapZone || this.isResizing) return;
      const rect = entries[0]?.contentRect;
      if (!rect) return;
      // O tamanho em CSS é border-box; a dimensão real só é medida quando o
      // browser informa uma mudança, nunca em getters executados pelo Angular.
      const bounds = element.getBoundingClientRect();
      if ((this.dialogWidth === null && bounds.width > 0) || (this.dialogHeight === null && bounds.height > 0)) {
        this.geometryCache = {
          ...this.geometryCache,
          width: this.dialogWidth === null ? bounds.width : this.geometryCache.width,
          height: this.dialogHeight === null ? bounds.height : this.geometryCache.height
        };
        this.freeGeometry = { ...this.geometryCache };
      }
    });
    this.intrinsicSizeObserver.observe(element);
  }

  /** Compatibilidade: o estilo das tabs é agora responsabilidade da taskbar. */
  configureTab(): void {}

  private setGeometry(geometry: DialogGeometry): void {
    this.dialogWidth = Math.round(geometry.width);
    this.dialogHeight = Math.round(geometry.height);
    this.dragPosition = { x: Math.round(geometry.x), y: Math.round(geometry.y) };
    this.geometryCache = {
      x: this.dragPosition.x, y: this.dragPosition.y,
      width: this.dialogWidth, height: this.dialogHeight
    };
    this.cd.markForCheck();
  }

  private rememberFreeGeometry(): void {
    if (!this.snapZone) this.freeGeometry = { ...this.currentGeometry };
  }

  private applySnap(zone: DialogSnapZone): void {
    if (!this.snapZone && !this.draggingFromSnap) this.rememberFreeGeometry();
    this.snapZone = zone;
    this.isMaximized = zone === 'maximized';
    this.setGeometry(geometryForSnap(zone, this.getViewport()));
    // Fixar/desafixar e reorganizar o foco nunca devem deixar a transformação
    // interna do CDK diferente da posição renderizada do diálogo.
    if (!this.manualSnapDrag) this.syncCdkFreeDragPosition();
    this._dialogStyleService.patchWindow(this.componentId, { maximized: this.isMaximized, snapped: true });
    this.cd.detectChanges();
  }

  private restoreFreeGeometry(): void {
    this.snapZone = null;
    this.isMaximized = false;
    const next = this.freeGeometry || this.currentGeometry;
    this.setGeometry(fitDialogToViewport(next, this.getViewport(), this.getMinWidth(), this.getMinHeight()));
    this._dialogStyleService.patchWindow(this.componentId, { maximized: false, snapped: false });
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.intrinsicSizeObserver?.disconnect();
    this.intrinsicSizeObserver = null;
    this.cancelManualSnappedDrag();
    this.stopGenie();
    if (this.openTimer !== null) clearTimeout(this.openTimer);
    this.hideSnapPreview();
    this.restoreFromPictureInPicture(true);
    this._dialogStyleService.removeMinimizeDialog(this.tabId);
    this._dialogStyleService.removeDialog(this.componentId);
    if (this.componentRef) this.componentRef.destroy();
  }

  loadChildComponent(componentType: Type<any>): void {
    const factory = this.componentFactoryResolver.resolveComponentFactory(componentType);
    const viewContainerRef = this.insertionPoint.viewContainerRef;
    viewContainerRef.clear();
    this.componentRef = viewContainerRef.createComponent(factory);
  }

  maximizeDialog(): void {
    if (this.isPictureInPicture) return;
    if (this.isMaximized) this.restoreFreeGeometry();
    else this.applySnap('maximized');
  }

  closeDialog(): void {
    if (this.destroyed) return;
    // Consultar o resultado antes de notificar a destruição do componente.
    const instance: any = this.componentRef?.instance;
    const result = instance && typeof instance.getDialogCloseResult === 'function'
      ? instance.getDialogCloseResult() : null;
    this._onClose.next(null);
    this.dialogRef.close(result);
  }

  /** Cancela animações pendentes e repõe o transform do Angular CDK. */
  private stopGenie(): void {
    ++this.genieSequence;
    this.activeGenie?.cancel();
    this.activeGenie = null;
  }

  /** Posição exata da tab; quando está no overflow usamos o botão +N. */
  private getDockRect(): GenieRect {
    const entries = document.querySelectorAll<HTMLElement>('[data-isdialog-window-id]');
    let item: HTMLElement | null = null;
    for (const entry of Array.from(entries)) {
      if (entry.getAttribute('data-isdialog-window-id') === this.componentId) {
        item = entry;
        break;
      }
    }
    if (!item) item = document.querySelector<HTMLElement>('.isdialog-taskbar__more');
    if (!item) item = document.querySelector<HTMLElement>('.isdialog-taskbar__label');
    const rect = item?.getBoundingClientRect();
    if (rect && rect.width > 0 && rect.height > 0) {
      return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
    }
    return { left: Math.max(0, window.innerWidth / 2 - 40),
      top: Math.max(0, window.innerHeight - 34), width: 80, height: 28 };
  }

  private toGenieRect(rect: DOMRect): GenieRect {
    return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
  }

  private readonly nextPaint = (): Promise<void> => new Promise(resolve => {
    window.requestAnimationFrame(() => resolve());
  });

  /**
   * Minimiza com animação leve de sucção para a taskbar.
   * O Angular real fica no DOM (hidden), com formulários e estado preservados.
   */
  async onMinimize(immediate = false): Promise<void> {
    if (this.isPictureInPicture || this.isMinimized || this.isMinimizing || this.destroyed) return;
    this.stopGenie();
    const sequence = this.genieSequence;
    if (!this.snapZone) this.rememberFreeGeometry();
    this.isRestoring = false;
    this.isOpening = false;
    this.isMinimizing = !immediate;
    const element = this.getDialogElement();

    const complete = () => {
      if (this.destroyed || sequence !== this.genieSequence) return;
      this.isMinimizing = false;
      this.isMinimized = true;
      this._dialogStyleService.minimizeDialog(this.tabId);
      this._dialogStyleService.patchWindow(this.componentId, { minimized: true });
      this.activeGenie = null;
      this.cd.detectChanges();
    };

    if (immediate || !element || this.genie.reducedMotion || !this._dialogStyleService.preferences.animations || this.data.genieAnimation === false) {
      complete();
      return;
    }

    // Publicar a tab antes do início para conhecer a sua posição real.
    this._dialogStyleService.patchWindow(this.componentId, { minimized: true });
    this.cd.detectChanges();
    const source = this.toGenieRect(element.getBoundingClientRect());

    // Um frame para a taskbar renderizar o novo item. Sem captura nem clone do DOM.
    await this.nextPaint();
    if (sequence !== this.genieSequence || this.destroyed) return;

    const animation = this.genie.play(element, source, this.getDockRect(), 'minimize');
    this.activeGenie = animation;
    await animation.finished;
    if (sequence !== this.genieSequence || this.destroyed) return;
    // Ocultar o elemento ANTES de retirar a animação evita um flash visual.
    complete();
    animation.cancel();
  }

  /** Restaura a animação a partir da posição da mesma tab. */
  async reopenDialog(): Promise<void> {
    if (this.isPictureInPicture) {
      this.pipWindow?.focus();
      return;
    }
    if (this.destroyed) return;
    if (!this.isMinimized && !this.isMinimizing) {
      this.onDialogClicked();
      return;
    }

    const wasMinimized = this.isMinimized;
    const dock = this.getDockRect(); // A tab vai desaparecer depois do patch.
    this.stopGenie();
    const sequence = this.genieSequence;
    this.isMinimizing = false;
    this.isMinimized = false;
    this.isRestoring = !this.genie.reducedMotion && this._dialogStyleService.preferences.animations && wasMinimized;
    this.isOpening = false;
    this.editTitle = false;
    this._dialogStyleService.removeMinimizeDialog(this.tabId);
    this._dialogStyleService.patchWindow(this.componentId, { minimized: false });
    this.onViewportResize();
    this.onDialogClicked();
    this.cd.detectChanges();

    const element = this.getDialogElement();
    if (!element || !this.isRestoring || this.data.genieAnimation === false) {
      this.isRestoring = false;
      this.cd.detectChanges();
      return;
    }

    const destination = this.toGenieRect(element.getBoundingClientRect());
    const animation = this.genie.play(element, destination, dock, 'restore');
    this.activeGenie = animation;
    await animation.finished;
    if (this.destroyed || sequence !== this.genieSequence) return;
    // O último quadro do restore já é o transform normal do CDK.
    this.isRestoring = false;
    this.cd.detectChanges();
    animation.cancel();
    this.activeGenie = null;
  }

  /** Mantido caso seja invocado noutras partes do projeto. */
  updateTabsPositions(): void {}

  /** Organização só dentro da biblioteca, sem conhecimento dos módulos de negócio. */
  arrangeInWorkspace(rect: DialogGeometry): void {
    if (this.destroyed || this.isPictureInPicture || this.isMinimized) return;
    this.cancelManualSnappedDrag();
    this.hideSnapPreview();
    this.snapZone = null;
    this.isMaximized = false;
    this.setGeometry(fitDialogToViewport(rect, this.getViewport(), this.getMinWidth(), this.getMinHeight()));
    this.freeGeometry = { ...this.currentGeometry };
    this._dialogStyleService.patchWindow(this.componentId, { maximized: false, snapped: false });
    this.cd.detectChanges();
  }

  centerDialog(): void {
    if (this.destroyed || this.isPictureInPicture) return;
    const rect = this.currentGeometry;
    const viewport = this.getViewport();
    this.arrangeInWorkspace({ ...rect,
      x: Math.max(0, (viewport.width - rect.width) / 2),
      y: Math.max(0, (viewport.height - rect.height) / 2)
    });
  }

  resetDialogSize(): void {
    if (!this.openingGeometry || this.isPictureInPicture || this.isMinimized) return;
    this.arrangeInWorkspace({ ...this.openingGeometry });
  }

  togglePin(event?: Event): void {
    event?.stopPropagation();
    if (this.isPictureInPicture || this.destroyed) return;
    // Fixar altera apenas foco/z-index. Não deve alterar a geometria nem
    // deixar a posição interna do CDK diferente da posição Angular.
    this._dialogStyleService.togglePin(this.componentId);
    if (!this.manualSnapDrag) this.syncCdkFreeDragPosition();
    this.cd.markForCheck();
  }

  private syncCdkFreeDragPosition(): void {
    if (this.cdkDrag && !this.isPictureInPicture) {
      this.cdkDrag.setFreeDragPosition({ ...this.dragPosition });
    }
  }

  onBarContextMenu(event: MouseEvent): void {
    if ((event.target as HTMLElement)?.closest('.window-controls, input, textarea, button, a')) return;
    event.preventDefault();
    this.windowActionsTrigger?.openMenu();
  }

  onDialogClicked(): void {
    // Durante o Genie, o registo é marcado como minimizado antes de terminar
    // a animação para criar a tab. Focar nesta fase restauraria imediatamente
    // a janela, cancelando a minimização.
    if (this.destroyed || this.isPictureInPicture || this.isMinimized || this.isMinimizing) return;
    this._dialogStyleService.updateZindex(this.componentId);
  }

  /**
   * Drag de janelas em snap/maximizadas.
   *
   * O CDK calcula a posição de pickup e o boundary no início do drag; mudar
   * a width/height no cdkDragStarted invalida essas coordenadas. Por isso,
   * nestas janelas, o CDK fica desativado ATÉ voltarem ao modo livre.
   * A partir do pointerdown, só restauramos o tamanho após o utilizador
   * mover efetivamente o ponteiro, para não interferir com duplo clique.
   */
  onSnappedWindowPointerDown(event: PointerEvent): void {
    if (this.isPictureInPicture || this.isResizing || this.isMinimized ||
        this.destroyed || event.button !== 0 || event.isPrimary === false) return;

    // Recuperação de segurança: um pointerup/cancel perdido não pode manter o
    // CdkDrag desativado nem aplicar a posição antiga no gesto seguinte.
    // Limpar antes do guard isSnapped, pois o drag em curso pode já ter reposto
    // snapZone=null quando o pointerup desapareceu.
    if (this.manualSnapDrag) {
      if (this.manualSnapDrag.pointerId !== event.pointerId) return;
      this.finishManualSnappedDragFromLastPoint(true);
    }
    if (!this.snapZone || this.isManualSnappedDrag) return;

    const target = event.target;
    if (target instanceof Element && target.closest(
      '.window-controls, a, button, input, select, textarea, [contenteditable="true"]'
    )) return;

    const element = this.getDialogElement();
    const bar = this.hostElement.nativeElement.querySelector<HTMLElement>('#' + this.windowBarId);
    if (!element || !bar) return;
    const rect = element.getBoundingClientRect();
    // Ao retirar um snap lateral/de canto, manter a dimensão VISÍVEL da janela.
    // Só uma janela maximizada deve voltar ao tamanho livre anterior; mudar
    // largura/altura num canto faria a barra fugir do ponto onde foi agarrada.
    const dragSize = this.snapZone === 'maximized'
      ? fitDialogToViewport(
          this.freeGeometry || this.currentGeometry,
          this.getViewport(), this.getMinWidth(), this.getMinHeight()
        )
      : { width: rect.width, height: rect.height };

    this.manualSnapDrag = {
      pointerId: event.pointerId,
      originalZone: this.snapZone,
      startX: event.clientX,
      startY: event.clientY,
      ratioX: Math.max(0.05, Math.min(0.95, (event.clientX - rect.left) / Math.max(1, rect.width))),
      gripY: Math.max(0, Math.min(34, event.clientY - rect.top)),
      width: dragSize.width,
      height: dragSize.height,
      started: false,
      exitedOriginalZone: false,
      clientX: event.clientX,
      clientY: event.clientY,
      frame: null,
      captureElement: bar
    };

    // Fora do Angular: pointermove não força change detection a cada evento.
    // Capturar no pointerdown e não apenas depois de 5px. Assim recebemos
    // pointerup/cancel mesmo quando o elemento se move durante o gesto.
    // O drag manual não altera a captura nem o CdkDrag entre frames.
    try { bar.setPointerCapture(event.pointerId); } catch { /* browser/elemento sem suporte */ }

    this.zone.runOutsideAngular(() => {
      window.addEventListener('pointermove', this.onManualSnappedPointerMove, { passive: false });
      window.addEventListener('pointerup', this.onManualSnappedPointerUp);
      window.addEventListener('pointercancel', this.onManualSnappedPointerCancel);
      window.addEventListener('blur', this.onManualSnappedWindowBlur);
      bar.addEventListener('lostpointercapture', this.onManualSnappedLostCapture);
    });
  }

  private readonly onManualSnappedPointerMove = (event: PointerEvent): void => {
    const state = this.manualSnapDrag;
    if (!state || event.pointerId !== state.pointerId) return;
    state.clientX = event.clientX;
    state.clientY = event.clientY;

    if (!state.started) {
      if (Math.hypot(event.clientX - state.startX, event.clientY - state.startY) < 5) return;
      state.started = true;
      // O CDK esteve desativado desde o pointerdown por existir snapZone.
      // Continua desativado enquanto este drag manual estiver ativo.
      this.zone.run(() => {
        this.isManualSnappedDrag = true;
        this.snapZone = null;
        this.isMaximized = false;
        this.onDialogClicked();
        const rect = this.getManualSnappedPosition(state.clientX, state.clientY, state);
        this.setGeometry(rect);
        this.cd.detectChanges();
      });
      state.captureElement.style.cursor = 'grabbing';
    }

    // Evitar seleção de texto ou drag nativo concorrente ao movimento manual.
    if (event.cancelable) event.preventDefault();

    if (state.frame === null) {
      state.frame = window.requestAnimationFrame(() => {
        state.frame = null;
        if (this.manualSnapDrag !== state || !state.started) return;
        this.moveManualSnappedDrag(state);
      });
    }
  };

  private getManualSnappedPosition(
    x: number, y: number,
    state: NonNullable<ISDialogComponent['manualSnapDrag']>
  ): DialogGeometry {
    return fitDialogToViewport({
      x: x - state.width * state.ratioX,
      y: y - state.gripY,
      width: state.width,
      height: state.height
    }, this.getViewport(), this.getMinWidth(), this.getMinHeight());
  }

  /** A posição é aplicada uma vez por frame, sem detetar alterações no conteúdo Angular. */
  private moveManualSnappedDrag(state: NonNullable<ISDialogComponent['manualSnapDrag']>): void {
    const geometry = this.getManualSnappedPosition(state.clientX, state.clientY, state);
    this.dragPosition = { x: Math.round(geometry.x), y: Math.round(geometry.y) };
    this.cdkDrag.setFreeDragPosition(this.dragPosition);

    const candidate = this._dialogStyleService.preferences.dragSnap ? detectDialogDragSnap(
      state.clientX, state.clientY,
      {
        left: geometry.x, top: geometry.y,
        right: geometry.x + geometry.width, bottom: geometry.y + geometry.height
      },
      this.getViewport()
    ) : null;

    // Ao largar perto do topo, não voltar imediatamente a maximizar.
    // Só reativamos o snap depois de sair da zona de snap original.
    if (candidate !== state.originalZone) state.exitedOriginalZone = true;
    const nextZone = state.exitedOriginalZone ? candidate : null;
    if (nextZone === this.dragPreviewZone) return;
    if (nextZone) {
      this.dragPreviewZone = nextZone;
      this.showSnapPreview(geometryForSnap(nextZone, this.getViewport()));
    } else {
      this.hideSnapPreview();
    }
  }

  private readonly onManualSnappedPointerUp = (event: PointerEvent): void => {
    this.finishManualSnappedDrag(event, false);
  };

  private readonly onManualSnappedPointerCancel = (event: PointerEvent): void => {
    this.finishManualSnappedDrag(event, true);
  };

  /** Também liberta o drag se a janela perder foco ou a captura do ponteiro. */
  private readonly onManualSnappedWindowBlur = (): void => {
    this.finishManualSnappedDragFromLastPoint(true);
  };

  private readonly onManualSnappedLostCapture = (event: PointerEvent): void => {
    if (this.manualSnapDrag?.pointerId === event.pointerId) {
      this.finishManualSnappedDragFromLastPoint(true);
    }
  };

  private finishManualSnappedDragFromLastPoint(cancelled: boolean): void {
    const state = this.manualSnapDrag;
    if (!state) return;
    // Esta chamada só precisa das últimas coordenadas guardadas; é usada para
    // a limpeza caso o browser não entregue um pointerup normal.
    this.finishManualSnappedDrag({
      pointerId: state.pointerId,
      clientX: state.clientX,
      clientY: state.clientY
    } as PointerEvent, cancelled);
  }

  private finishManualSnappedDrag(event: PointerEvent, cancelled: boolean): void {
    const state = this.manualSnapDrag;
    if (!state || event.pointerId !== state.pointerId) return;
    if (state.frame !== null) {
      window.cancelAnimationFrame(state.frame);
      state.frame = null;
    }
    state.clientX = event.clientX;
    state.clientY = event.clientY;
    if (state.started) this.moveManualSnappedDrag(state);
    const finalZone = cancelled ? null : this.dragPreviewZone;
    const wasDragging = state.started;
    const finalPosition = wasDragging ? this.getManualSnappedPosition(
      state.clientX, state.clientY, state
    ) : null;

    this.cancelManualSnappedDrag();
    if (!wasDragging || this.destroyed) return;

    this.zone.run(() => {
      if (finalZone) {
        this.applySnap(finalZone);
      } else if (finalPosition) {
        this.snapZone = null;
        this.isMaximized = false;
        this.setGeometry(finalPosition);
        this.syncCdkFreeDragPosition();
        this.freeGeometry = { ...finalPosition };
        this._dialogStyleService.patchWindow(this.componentId, { maximized: false, snapped: false });
        this.cd.detectChanges();
      }
    });
  }

  private cancelManualSnappedDrag(): void {
    const state = this.manualSnapDrag;
    if (!state) return;
    this.manualSnapDrag = null;
    window.removeEventListener('pointermove', this.onManualSnappedPointerMove);
    window.removeEventListener('pointerup', this.onManualSnappedPointerUp);
    window.removeEventListener('pointercancel', this.onManualSnappedPointerCancel);
    window.removeEventListener('blur', this.onManualSnappedWindowBlur);
    state.captureElement.removeEventListener('lostpointercapture', this.onManualSnappedLostCapture);
    if (state.frame !== null) window.cancelAnimationFrame(state.frame);
    try {
      if (state.captureElement.hasPointerCapture(state.pointerId)) {
        state.captureElement.releasePointerCapture(state.pointerId);
      }
    } catch { /* O elemento pode ter sido removido do DOM */ }
    state.captureElement.style.cursor = '';
    this.hideSnapPreview();
    this.isManualSnappedDrag = false;
  }

  onDragStart(_event: any): void {
    if (this.isPictureInPicture) return;
    this.onDialogClicked();
    const bar = this.hostElement.nativeElement.querySelector<HTMLElement>('#' + this.windowBarId);
    if (bar) bar.style.cursor = 'grabbing';
    // O CDK já mediu o elemento. Nunca alteramos aqui width/height nem
    // dragPosition; o caso snapped é gerido desde pointerdown acima.
    this.draggingFromSnap = false;
  }

  /**
   * O cursor pode ficar afastado da borda quando o CDK bloqueia o movimento
   * do retângulo. Usamos as arestas reais + a posição do cursor para o snap.
   * É a mesma deteção no preview e no drop, evitando resultados diferentes.
   */
  private getDragSnapZone(pageX: number, pageY: number): DialogSnapZone | null {
    if (!this._dialogStyleService.preferences.dragSnap) return null;
    const rect = this.getDialogElement()?.getBoundingClientRect();
    if (!rect) return null;
    return detectDialogDragSnap(
      pageX - window.scrollX,
      pageY - window.scrollY,
      { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom },
      this.getViewport()
    );
  }

  onDragMoved(event: CdkDragMove): void {
    if (this.isPictureInPicture || this.isResizing) return;
    const zone = this.getDragSnapZone(event.pointerPosition.x, event.pointerPosition.y);
    if (zone === this.dragPreviewZone) return;
    this.dragPreviewZone = zone;
    if (!zone) { this.hideSnapPreview(); return; }
    this.showSnapPreview(geometryForSnap(zone, this.getViewport()));
  }

  onDragEnd(event: CdkDragEnd): void {
    const bar = this.hostElement.nativeElement.querySelector<HTMLElement>('#' + this.windowBarId);
    if (bar) bar.style.cursor = 'grab';
    // O CDK pode repor o transform perto do fim do drag. Nesse caso usamos
    // a última zona mostrada na preview, que corresponde ao último movimento.
    const zone = this.getDragSnapZone(event.dropPoint.x, event.dropPoint.y)
      ?? this.dragPreviewZone;
    this.hideSnapPreview();
    // Deixar o CDK terminar o próprio drag antes de fixar a nova posição.
    setTimeout(() => {
      if (this.destroyed || this.isPictureInPicture) return;
      if (zone) {
        this.applySnap(zone);
      } else {
        const rect = this.getDialogElement()?.getBoundingClientRect();
        if (rect) {
          const freeSize = this.draggingFromSnap ? this.freeGeometry : null;
          const corrected = fitDialogToViewport({
            x: rect.left, y: rect.top,
            width: freeSize?.width ?? rect.width,
            height: freeSize?.height ?? rect.height
          }, this.getViewport(), this.getMinWidth(), this.getMinHeight());
          this.snapZone = null;
          this.isMaximized = false;
          this.setGeometry(corrected);
          this.freeGeometry = { ...corrected };
          this._dialogStyleService.patchWindow(this.componentId, { maximized: false, snapped: false });
          this.cd.detectChanges();
        }
      }
      this.draggingFromSnap = false;
    }, 0);
  }

  private showSnapPreview(geometry: DialogGeometry): void {
    if (!this.snapPreview) {
      this.snapPreview = document.createElement('div');
      this.snapPreview.className = 'isdialog-snap-preview';
      Object.assign(this.snapPreview.style, {
        position: 'fixed', pointerEvents: 'none', zIndex: '18000',
        background: 'rgba(74, 116, 174, .16)', border: '2px solid rgba(74, 116, 174, .65)',
        borderRadius: '9px', boxSizing: 'border-box', transition: 'all 90ms ease',
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.3)'
      });
      document.body.appendChild(this.snapPreview);
    }
    Object.assign(this.snapPreview.style, {
      left: geometry.x + 'px', top: geometry.y + 'px',
      width: geometry.width + 'px', height: geometry.height + 'px'
    });
  }

  private hideSnapPreview(): void {
    this.snapPreview?.remove();
    this.snapPreview = null;
    this.dragPreviewZone = null;
  }

  onResizeStart(): void {
    // O utilizador pode redimensionar dialogs originalmente com width/height automáticos.
    // O cache fornece o tamanho real, sem leituras por cada handle.
    this.isResizing = true;
    this.onDialogClicked();
    this.snapZone = null;
    this.isMaximized = false;
    this._dialogStyleService.patchWindow(this.componentId, { maximized: false, snapped: false });
  }

  onResizeRectChange(rect: DialogGeometry): void {
    this.setGeometry(rect);
    this.freeGeometry = { ...rect };
    this.cd.detectChanges();
  }

  onResizeEnd(): void {
    this.isResizing = false;
    this.freeGeometry = { ...this.currentGeometry };
  }

  private snapFromMenu(zone: DialogSnapZone): void { this.applySnap(zone); }
  moveDialogToTopRight(): void { this.snapFromMenu('top-right'); }
  moveDialogToTopLeft(): void { this.snapFromMenu('top-left'); }
  moveDialogToBottomRight(): void { this.snapFromMenu('bottom-right'); }
  moveDialogToBottomLeft(): void { this.snapFromMenu('bottom-left'); }
  moveDialogToLeft(): void { this.snapFromMenu('left'); }
  moveDialogToRight(): void { this.snapFromMenu('right'); }

  onOpacityChange(event: any) {
    let dialog = this.getDialogElement();
    this.currentOpacity = 1 - event / 100;
    if (this.currentOpacity > 0.96) {
      this.currentOpacity = 1;
    }
    if (dialog) {
      dialog.style.opacity = this.currentOpacity.toString();
    }
    this._ref.detectChanges();
  }

  /** Procura elementos dentro do host (funciona no DOM original e no PiP). */
  private getDialogElement(): HTMLElement | null {
    return this.hostElement.nativeElement.querySelector<HTMLElement>('#' + this.componentId);
  }

  private getTitleInput(): HTMLInputElement | null {
    return this.hostElement.nativeElement.querySelector<HTMLInputElement>(
      'input[id="' + this.tabTitleId + 'input"]'
    );
  }

  /**
   * Transfere o host do componente, e não apenas uma cópia do HTML.
   * Assim, instâncias Angular, formulários e subscriptions permanecem vivas.
   * A chamada requestWindow ocorre diretamente no evento click do utilizador.
   */
  async togglePictureInPicture(event?: Event): Promise<void> {
    event?.stopPropagation();
    if (this.manualSnapDrag) this.cancelManualSnappedDrag();
    this.pictureInPictureError = null;

    if (this.pipWindow) {
      this.restoreFromPictureInPicture(true);
      return;
    }

    const api = (window as WindowWithDocumentPip).documentPictureInPicture;
    if (!this.isPictureInPictureSupported || !api) {
      this.pictureInPictureError = 'Picture-in-Picture não está disponível neste browser.';
      return;
    }

    if (api.window) {
      this.pictureInPictureError = 'Só pode existir uma janela Picture-in-Picture por separador.';
      return;
    }

    const host = this.hostElement.nativeElement;
    const dialog = this.getDialogElement();
    if (!dialog || !host.parentNode) return;

    const bounds = dialog.getBoundingClientRect();
    // Solicitar o tamanho REAL atual da janela (incluindo resize/snap).
    // O Chrome/Edge pode ajustar estes valores aos limites da PiP no monitor.
    const width = Math.max(1, Math.round(bounds.width));
    const height = Math.max(1, Math.round(bounds.height));

    try {
      const pipWindow = await api.requestWindow({ width, height });
      if (this.destroyed) {
        pipWindow.close();
        return;
      }

      // Guardar a posição original, para restaurar a mesma instância Angular.
      const placeholder = document.createComment('is-dialog-pip-placeholder');
      host.parentNode!.insertBefore(placeholder, host);
      this.pipPlaceholder = placeholder;
      this.pipWindow = pipWindow;

      this.pipPageHideHandler = () => this.restoreFromPictureInPicture(false);
      pipWindow.addEventListener('pagehide', this.pipPageHideHandler);

      this.copyStylesToPictureInPicture(pipWindow);
      this.copyThemeAttributes(pipWindow.document);
      this.setupPictureInPictureLayout(pipWindow.document);

      // appendChild adopta o nó no novo Document; não recria Angular.
      pipWindow.document.body.appendChild(host);
      this.pipOverlayContainer.moveTo(pipWindow.document);

      this.isPictureInPicture = true;
      this._dialogStyleService.patchWindow(this.componentId, { inPictureInPicture: true });
      this.cd.detectChanges();
    } catch (error) {
      console.error('Falha ao destacar ISDialog para Picture-in-Picture:', error);
      if (this.pipWindow) {
        this.restoreFromPictureInPicture(true);
      }
      this.pictureInPictureError = 'Não foi possível destacar a janela. Verifica as permissões do Chrome.';
      if (!this.destroyed) this.cd.detectChanges();
    }
  }

  /** Devolve o host ao mesmo sítio quando o utilizador fecha a PiP. */
  private restoreFromPictureInPicture(closePipWindow: boolean): void {
    const pipWindow = this.pipWindow;
    if (!pipWindow) return;

    // Anular primeiro para tornar o handler pagehide idempotente.
    this.pipWindow = null;
    this.pipStylesObserver?.disconnect();
    this.pipStylesObserver = null;

    if (this.pipPageHideHandler) {
      pipWindow.removeEventListener('pagehide', this.pipPageHideHandler);
      this.pipPageHideHandler = null;
    }

    const host = this.hostElement.nativeElement;
    if (this.pipPlaceholder?.parentNode) {
      this.pipPlaceholder.parentNode.replaceChild(host, this.pipPlaceholder);
    } else if (!this.destroyed) {
      document.body.appendChild(host);
    }
    this.pipPlaceholder = null;

    // Importante: regressar apenas o OverlayContainer DESTE dialog.
    if (!this.destroyed) this.pipOverlayContainer.moveTo(document);

    this.isPictureInPicture = false;
    this._dialogStyleService.patchWindow(this.componentId, { inPictureInPicture: false });
    if (!this.destroyed) {
      this.onViewportResize();
      this.onDialogClicked();
    }
    if (closePipWindow && !pipWindow.closed) pipWindow.close();
    if (!this.destroyed) this.cd.detectChanges();
  }

  /** Replica CSS global e estilos de componentes que Angular injeta no head. */
  private copyStylesToPictureInPicture(pipWindow: Window): void {
    const targetDoc = pipWindow.document;
    const base = targetDoc.createElement('base');
    base.href = document.baseURI;
    targetDoc.head.appendChild(base);

    const copyNode = (node: Element) => {
      if (!node.matches('style, link[rel="stylesheet"]')) return;
      const clone = node.cloneNode(true) as Element;
      if (node instanceof HTMLLinkElement && clone instanceof HTMLLinkElement) {
        clone.href = node.href;
      }
      targetDoc.head.appendChild(clone);
    };

    document.head.querySelectorAll('style, link[rel="stylesheet"]').forEach(copyNode);

    // Suporta CSS injetado depois (ex.: módulos lazy-loaded do Angular).
    this.pipStylesObserver = new MutationObserver(records => {
      for (const record of records) {
        record.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) copyNode(node as Element);
        });
      }
    });
    this.pipStylesObserver.observe(document.head, { childList: true });
  }

  /** Conserva classes, direções e atributos de tema usados pelo layout. */
  private copyThemeAttributes(targetDoc: Document): void {
    const attributes = ['class', 'lang', 'dir', 'data-bs-theme', 'data-theme'];
    for (const attribute of attributes) {
      for (const pair of [
        [document.documentElement, targetDoc.documentElement],
        [document.body, targetDoc.body]
      ]) {
        const value = pair[0].getAttribute(attribute);
        if (value !== null) pair[1].setAttribute(attribute, value);
      }
    }
  }

  /** No PiP o Chrome controla o drag/resize nativos da janela. */
  private setupPictureInPictureLayout(targetDoc: Document): void {
    const styles = targetDoc.createElement('style');
    styles.textContent = `
      html, body { margin: 0 !important; width: 100%; height: 100%; overflow: hidden; }
      body > *:not(.cdk-overlay-container) { display: block; width: 100%; height: 100%; }
      .dummy-container { display: none !important; }
      .dialog {
        position: relative !important;
        left: 0 !important; top: 0 !important;
        transform: none !important;
        width: 100% !important; height: 100% !important;
        min-width: 0 !important; min-height: 0 !important;
        max-width: none !important; max-height: none !important;
        box-shadow: none !important; border-radius: 0 !important;
      }
      .dialog-body { min-height: 0; overflow: auto; }
      .window-bar { border-radius: 0 !important; cursor: default !important; }
      .resize-icon, .isdialog-resize-handle { display: none !important; }
    `;
    targetDoc.head.appendChild(styles);
  }

  generateUniqueId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
  }

  changeTitle(event?: Event) {
    event?.stopPropagation();
    this.editTitle = true;

    let tabTitle = this.getTitleInput();
    if (tabTitle) {
      tabTitle.click();
      tabTitle.focus();

    }
  }

  /**
   * Saves the dialog title change
   */
  saveTitleChange() {
    // Casts the HTML element found to a HTMLInputElement to be able to access its value
    let tabTitle = this.getTitleInput();
    if (tabTitle) {
      this.data.title = tabTitle.value;
      this._dialogStyleService.patchWindow(this.componentId, { title: this.data.title || 'Janela' });
    }
    this.editTitle = false;
  }

  onTitleChange(value: any) {
    this.data.title = value;
    this._dialogStyleService.patchWindow(this.componentId, { title: value || 'Janela' });
  }

  onVisibilityClick() {
    setTimeout(() => {
      const progressBar = document.querySelector('.mat-mdc-progress-bar') as HTMLElement;
      if (progressBar) {
        progressBar.style.setProperty('--mdc-linear-progress-active-indicator-color', 'red');
      }
    }, 20);

  }

  onDummyContainerClick(ev: any) {

  }
}